// @flow

import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'
import { combine, createStore } from '@reatom/core'
import { context } from '@reatom/react'
import { ToastProvider } from 'react-toast-notifications'
import { BrowserRouter } from 'react-router-dom'
import axios from 'axios'

import { loadDetectedLanguage } from './i18n'

import { StepsAtom } from '@bfg/atoms/Steps'
import { ImagesAtom } from '@bfg/atoms/FileUpload'
import { GeneratorAtom } from '@bfg/atoms/Generator'
import { KidConfigAtom } from '@bfg/atoms/KidConfig'
import { AppConfigAtom, InitApp } from '@bfg/atoms/AppConfig'
import { ArticlesConfigAtom } from '@bfg/atoms/ArticlesConfig'
import { CelebsAtom } from '@bfg/atoms/Celebs'
import { StripeApplePaymentAtom } from '@bfg/atoms/payment_systems/stripe/StripePayment'
import { GooglePaymentAtom } from '@bfg/atoms/payment_systems/stripe/GooglePayment'
import { SubscriberAtom, SubscriberLoaded } from '@bfg/atoms/Subscriber'
import { DebugAtom } from '@bfg/atoms/Debug'
import { WebViewDetectorAtom } from '@bfg/atoms/WebViewDetector'
import { UtmAnalytics, SetRwReferral } from '@bfg/atoms/UtmAnalytics'
import { messagesAtom } from '@bfg/atoms/Util'
import { PaymentAtom, GetPaymentType } from '@bfg/atoms/Payment'
import { api, debugMode } from '@bfg/common'
import Wrapper from '@bfg/components/Wrapper'
import { CardPaymentAtom } from '@bfg/atoms/payment_systems/stripe/CardPayment'
import { WalletTypeAtom } from '@bfg/atoms/WalletType'
import { PaywallVariantAtom } from '@bfg/atoms/PaywallVariant'
import { ExperimentsAtom } from '@bfg/atoms/Experiments'
import { NewRelic } from '@bfg/atoms/NewRelic'
import { BillingReceiptsAtom } from '@bfg/atoms/BillingReceipts'
import { ApplePaymentAtom as BraintreeApplePaymentAtom } from '@bfg/atoms/payment_systems/braintree/ApplePayment'
import { GooglePaymentAtom as BraintreeGooglePaymentAtom } from '@bfg/atoms/payment_systems/braintree/GooglePayment'
import { ApplePaymentAtom as BluesnapApplePaymentAtom } from '@bfg/atoms/payment_systems/bluesnap/ApplePayment'
import { GooglePaymentAtom as BluesnapGooglePaymentAtom } from '@bfg/atoms/payment_systems/bluesnap/GooglePayment'

const rootAtom = combine({
  appConfig: AppConfigAtom,
  articlesConfigAtom: ArticlesConfigAtom,
  currentStep: StepsAtom,
  images: ImagesAtom,
  payment: GeneratorAtom,
  kidConfig: KidConfigAtom,
  celebs: CelebsAtom,
  applePay: StripeApplePaymentAtom,
  btApplePay: BraintreeApplePaymentAtom,
  bsApplePay: BluesnapApplePaymentAtom,
  googlePay: GooglePaymentAtom,
  btGooglePay: BraintreeGooglePaymentAtom,
  bsGooglePay: BluesnapGooglePaymentAtom,
  subscriber: SubscriberAtom,
  debug: DebugAtom,
  webview: WebViewDetectorAtom,
  utm: UtmAnalytics,
  messages: messagesAtom,
  paymentState: PaymentAtom,
  cardPayment: CardPaymentAtom,
  walletType: WalletTypeAtom,
  experiments: ExperimentsAtom,
  newrelic: NewRelic,
  billingReceipts: BillingReceiptsAtom,
  paywallVariant: PaywallVariantAtom,
})

const store = createStore(
  rootAtom, {},
  window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__()
)

store.dispatch(InitApp())

// Rewardful: rw.js асинхронно резолвит referral UUID и зовёт ready.
// referral есть только если Rewardful подтвердил аффилиата (фейковый ?via= его не даёт).
if (typeof window.rewardful === 'function') {
  window.rewardful('ready', () => {
    if (window.Rewardful && window.Rewardful.referral) {
      // 1) кладём UUID в atom — payload оплаты подхватит его на момент клика
      store.dispatch(SetRwReferral(window.Rewardful.referral))
      // 2) перезапрашиваем планы: теперь GetPaymentType увидит rwReferral и попросит 'rewardful'.
      //    При первой загрузке (до ready) показывался default — страница не зависит от rw.js.
      store.dispatch(GetPaymentType())
    }
  })
}

store.subscribe(SubscriberLoaded, (payload) => {
  const gateway = store.getState(PaymentAtom)
  const utm = store.getState(UtmAnalytics)
  const urlParams = new URLSearchParams(window.location.search)
  const data = new FormData()
  data.append('source', utm.utmSource)
  data.append('medium', utm.utmMedium)
  data.append('campaign', utm.utmCampaign)
  data.append('gateway', gateway.gateway)
  data.append('fbclid', urlParams.get('fbclid'))
  axios.post(`${api}/subscriber/${payload.uid}`, data)
    .catch(console.error)
})

export const App = () => {
  useEffect(() => {
    if (debugMode === 'true') {
      import('@reatom/debug').then((reatomDebug) => {
        reatomDebug.connectReduxDevtools(store)
      })
    }
  }, [])

  return (
    <context.Provider value={store}>
      <ToastProvider placement="bottom-right">
        <BrowserRouter>
          <Wrapper/>
        </BrowserRouter>
      </ToastProvider>
    </context.Provider>
  )
}

const rootContainer = document.getElementById('app')
if (rootContainer) {
  // Монтируем после того, как приедет перевод нужного языка: он лежит в
  // отдельном чанке, и без ожидания на не-английской странице мелькнул бы
  // английский текст. Задержка не видна — в #app уже лежит статический снимок
  // первого экрана, так что белого экрана нет.
  //
  // finally, а не then: если чанк не доедет, приложение обязано стартовать
  // на английском фолбэке, а не остаться неотрисованным.
  loadDetectedLanguage().finally(() => {
    ReactDOM.render(<App/>, rootContainer)
  })
} else {
  document.querySelector('body')?.prepend('<p>cant find tag with id app</p>')
}

/**

 Добавить в <head>
 <script type="text/javascript">
 var _iub = _iub || [];
 _iub.csConfiguration = {
 "lang":"en",
 "enableCcpa":true,
 "countryDetection":true,
 "gdprAppliesGlobally":false,
 "siteId":1759798,
 "cookiePolicyId":56731815,
 "banner":{
 "position":"bottom",
 "textColor":"black",
 "backgroundColor":"white",
 "acceptButtonDisplay":true,
 "acceptButtonColor":"#0073CE",
 "acceptButtonCaptionColor":"white",
 "customizeButtonDisplay":true,
 "customizeButtonColor":"#DADADA",
 "customizeButtonCaptionColor":"#4D4D4D"
 }
 };
 </script>
 <script type="text/javascript" src="//cdn.iubenda.com/cs/iubenda_cs.js" charset="UTF-8" async></script>

 Добавить в подвал страницы:
 a) Privacy Policy
 <a href="https://www.iubenda.com/privacy-policy/56731815" class="iubenda-white no-brand iubenda-embed iub-body-embed" title="Privacy Policy">Privacy Policy</a><script type="text/javascript">(function (w,d) {var loader = function () {var s = d.createElement("script"), tag = d.getElementsByTagName("script")[0]; s.src="https://cdn.iubenda.com/iubenda.js"; tag.parentNode.insertBefore(s,tag);}; if(w.addEventListener){w.addEventListener("load", loader, false);}else if(w.attachEvent){w.attachEvent("onload", loader);}else{w.onload = loader;}})(window, document);</script>

 b) Cookie Policy
 <a href="https://www.iubenda.com/privacy-policy/56731815/cookie-policy" class="iubenda-white no-brand iubenda-embed iub-body-embed" title="Cookie Policy">Cookie Policy</a><script type="text/javascript">(function (w,d) {var loader = function () {var s = d.createElement("script"), tag = d.getElementsByTagName("script")[0]; s.src="https://cdn.iubenda.com/iubenda.js"; tag.parentNode.insertBefore(s,tag);}; if(w.addEventListener){w.addEventListener("load", loader, false);}else if(w.attachEvent){w.attachEvent("onload", loader);}else{w.onload = loader;}})(window, document);</script>

 c) <a href="javascript:void(0)" class="iubenda-ccpa-opt-out">Do Not Sell My Personal Information</a>

 */